// ─────────────────────────────────────────────────────────────────────────────
// 🚨 EITHER THE WHOLE WIRE, OR NONE OF IT.
//
// Jesse: "WireInstructionsModal: confirm what renders when instructions are
// incomplete. It must show no partial wire details at all: one clear message
// that instructions aren't ready and not to send money until they appear."
//
// 🚨 WHAT IT USED TO RENDER. All six rows, every absent value reading "Not
// available yet" — and the Reference / Memo row filled in with a REAL computed
// reference ("DP-<loan>-<LAST4>"), because that one has a client-side fallback.
// Plus the wire amount in the header, the "Amount to Wire" breakdown, and a
// deadline instruction: "Wire must be received by 2:00 PM local time on the
// deadline date." A borrower looking at that sees a wire form with a real
// amount, a real reference and a real deadline, two or three real bank fields,
// and one line of amber text. The only thing actually withheld was the Copy
// button.
//
// 🔑 THE PARTIAL CASE IS THE DANGEROUS ONE, NOT THE EMPTY CASE. Nobody wires
// money from a blank screen. A screen showing a bank name, an account name and
// an account number with a routing number "not available yet" invites a phone
// call asking for the missing piece — and that call is the opening a wire-fraud
// impersonator needs, because the borrower has already accepted that the rest
// of the instruction is genuine.
//
// 🔑 SO READINESS IS ALL-OR-NOTHING AND LIVES HERE, not in the component. The
// server's buildWireInstructions is already all-or-nothing (option-period.js,
// closing.js); this is the same rule at the last point before a human reads it,
// and it is a pure function so every single-field-missing case can be enumerated
// in a test rather than reasoned about.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 🚨 THE FIELDS A WIRE CANNOT BE SENT WITHOUT.
 *
 * ⚠️ `reference` IS NOT ONE OF THEM, and that is the subtle part. It has a
 * client-side fallback, so it is always present — including when nothing else
 * is. Counting it would make a payload of nothing but a memo line look one
 * field short of ready.
 */
export const WIRE_REQUIRED_FIELDS = Object.freeze([
  'bank_name', 'bank_address', 'account_name', 'account', 'routing',
]);
export const EXPECTED_WIRE_REQUIRED_FIELDS = 5;

/** Which required fields are absent. Named, so a staff log can say which. */
export function missingWireFields(wireData) {
  const d = wireData || {};
  return WIRE_REQUIRED_FIELDS.filter((k) => {
    const v = d[k];
    return v === null || v === undefined || String(v).trim() === '';
  });
}

/**
 * 🚨 THE GATE. False unless every required field is present.
 *
 * ⚠️ AN `error` ON THE PAYLOAD IS ALSO NOT READY. The server returns
 * `{ error, missing[] }` when its env vars are unset, and that object has no
 * field keys at all — so this would already be false. Checked explicitly
 * anyway, because a future payload could carry both.
 */
export function wireInstructionsReady(wireData) {
  if (!wireData || wireData.error) return false;
  return missingWireFields(wireData).length === 0;
}

/**
 * 🚨 THE ONE MESSAGE. Written as data rather than JSX so the words are
 * assertable and cannot drift between two render paths.
 *
 * ⚠️ IT SAYS "DO NOT SEND" IN THOSE WORDS. The old copy said instructions
 * "are not yet available" and that Doorly "will update them once your closing
 * details are finalized" — an informational sentence that tells a borrower to
 * wait, not an instruction not to pay. The failure this exists to prevent is a
 * borrower who wires money somewhere else in the meantime.
 */
export const WIRE_NOT_READY = Object.freeze({
  heading: 'Wire instructions aren\'t ready yet',
  body: 'Do not send any money until the full instructions appear on this screen. '
    + 'Doorly will tell you when they are ready.',
  fraud: 'If anyone sends you wire instructions for your Doorly purchase by email, '
    + 'text or phone before then, they are not from us. Call Doorly at 512-713-6016 '
    + 'before you act on them.',
});
